import { Maybe, Label, UserType } from '../../../__generated__/globalTypes'

import { PlanFragment, isPlanFragment } from './PlanFragment'

import { AddressFragment, isAddressFragment } from './AddressFragment'

import { Storefront, isStorefront } from './Storefront'

import {
  MinimalSponsorFragment,
  isMinimalSponsorFragment,
} from './MinimalSponsorFragment'

import { StripeSubscription, isStripeSubscription } from './StripeSubscription'

import { ExpenseReload, isExpenseReload } from './ExpenseReload'

import {
  PromptingsCoachFragment,
  isPromptingsCoachFragment,
} from './PromptingsCoachFragment'

import { Amount, isAmount } from './Amount'

import {
  StripeSourceFragment,
  isStripeSourceFragment,
} from './StripeSourceFragment'

import {
  UserSettingsFragment,
  isUserSettingsFragment,
} from './UserSettingsFragment'

import accountFragmentRawString from '../AccountFragment.graphql'

export const accountFragmentString = accountFragmentRawString

export type AccountFragment = {
  __typename: 'Account'
  id: string
  username: string
  firstName: string
  lastName: string
  email: string
  profileImageUrl: Maybe<string>
  isStaff: boolean
  phoneNumber: string
  isAffiliate: boolean
  isNewUser: boolean
  createdAt: string
  genealogyId: number
  stripeCustomerId: Maybe<string>
  dailyHeartfeltSends: number
  hasUnlimitedHeartfeltSends: boolean
  loyaltyRewards: number
  groupSends: number
  heartfeltSends: number
  heartfeltSystemSends: number
  scheduledSends: number
  systemSends: number
  cardPackageSends: number
  pointsTransitionSends: number
  employeeSends: number
  giftSends: number
  pointPrice: number
  promptingProLoginUrl: string
  promptinguLoginUrl: Maybe<string>
  streamMarketingLoginUrl: Maybe<string>
  promptinguJwt: string
  token: string
  hasBundlePlan: boolean
  affiliateUrl: string
  couldQualifyForFirstFreeCard: boolean
  isQualifiedForFirstFreeCard: boolean
  isTrialAffiliate: boolean
  isEligibleForFreeGsAffiliateAccount: boolean
  isEmailVerified: boolean
  labels: Label[]
  type: UserType
  cardCost: number
  bulkCardCost: number
  giftDiscount: number
  isExpenseReloadRequired: boolean
  isRep: boolean
  dateJoined: string
  hasFullAccessContactManager: boolean
  hasFullAccessFreeAffiliate: boolean
  shouldDisplayUpsale: boolean
  activePlansAndAddons: Maybe<PlanFragment>[]
  settings: Maybe<UserSettingsFragment>
  shippingAddress: Maybe<AddressFragment>
  sponsor: Maybe<MinimalSponsorFragment>
  storefront: Maybe<Storefront>
  plan: PlanFragment
  stripeSource: Maybe<StripeSourceFragment>
  stripeSubscription: Maybe<StripeSubscription>
  subscriptions: StripeSubscription[]
  points: Amount
  expense: Amount
  expenseReload: Maybe<ExpenseReload>
  verifiedPhoneNumber: Maybe<{
    __typename: 'PhoneNumber'
    nationalNumber: string
  }>
  promptingsCoach: Maybe<PromptingsCoachFragment>
}

export const isAccountFragment = (fragment: any): fragment is AccountFragment =>
  fragment &&
  fragment.__typename === 'Account' &&
  typeof fragment.id === 'string' &&
  typeof fragment.username === 'string' &&
  typeof fragment.firstName === 'string' &&
  typeof fragment.lastName === 'string' &&
  typeof fragment.email === 'string' &&
  (fragment.profileImageUrl === null ||
    typeof fragment.profileImageUrl === 'string') &&
  typeof fragment.isStaff === 'boolean' &&
  typeof fragment.phoneNumber === 'string' &&
  typeof fragment.isAffiliate === 'boolean' &&
  typeof fragment.isNewUser === 'boolean' &&
  typeof fragment.createdAt === 'string' &&
  typeof fragment.genealogyId === 'number' &&
  (fragment.stripeCustomerId === null ||
    typeof fragment.stripeCustomerId === 'string') &&
  typeof fragment.dailyHeartfeltSends === 'number' &&
  typeof fragment.hasUnlimitedHeartfeltSends === 'boolean' &&
  typeof fragment.loyaltyRewards === 'number' &&
  typeof fragment.groupSends === 'number' &&
  typeof fragment.heartfeltSends === 'number' &&
  typeof fragment.heartfeltSystemSends === 'number' &&
  typeof fragment.scheduledSends === 'number' &&
  typeof fragment.systemSends === 'number' &&
  typeof fragment.cardPackageSends === 'number' &&
  typeof fragment.pointsTransitionSends === 'number' &&
  typeof fragment.employeeSends === 'number' &&
  typeof fragment.giftSends === 'number' &&
  typeof fragment.pointPrice === 'number' &&
  typeof fragment.promptingProLoginUrl === 'string' &&
  (fragment.promptinguLoginUrl === null ||
    typeof fragment.promptinguLoginUrl === 'string') &&
  (fragment.streamMarketingLoginUrl === null ||
    typeof fragment.streamMarketingLoginUrl === 'string') &&
  typeof fragment.promptinguJwt === 'string' &&
  typeof fragment.token === 'string' &&
  typeof fragment.hasBundlePlan === 'boolean' &&
  typeof fragment.affiliateUrl === 'string' &&
  typeof fragment.couldQualifyForFirstFreeCard === 'boolean' &&
  typeof fragment.isQualifiedForFirstFreeCard === 'boolean' &&
  typeof fragment.isTrialAffiliate === 'boolean' &&
  typeof fragment.isEligibleForFreeGsAffiliateAccount === 'boolean' &&
  typeof fragment.isEmailVerified === 'boolean' &&
  Array.isArray(fragment.labels) &&
  fragment.labels
    .slice(0, 5)
    .reduce(
      (accum: any, next: any) =>
        accum &&
        (next === 'ZERO_POINT' ||
          next === 'AFFILIATE' ||
          next === 'STAFF' ||
          next === 'PRODUCT_LAUNCH' ||
          next === 'GIFTING_COMMITTEE' ||
          next === 'NOWSITE_ACTIVE' ||
          next === 'NOWSITE_NEED_SETUP' ||
          next === 'AFFILIATE_MISSING_W9' ||
          next === 'AFFILIATE_MISSING_W8' ||
          next === 'NEEDS_GS_AFFILIATE_OPT_IN' ||
          next === 'NEEDS_CUSTOMER_REFERRAL_OPT_IN' ||
          next === 'SHOULD_HIDE_REVOLUTION_OFFICE' ||
          next === 'GSA_SUBSCRIPTION_PAYMENT_FAILURE' ||
          next === 'PRIOR_AFFILIATE' ||
          next === 'HAS_INVALID_EMAIL' ||
          next === 'HAS_DUPLICATE_EMAIL' ||
          next === 'HAS_DUPLICATE_EMAIL_CLAIMED' ||
          next === 'HAS_FREE_CAMPAIGN_STORE' ||
          next === 'HAS_RELATIONSHIP_MARKETING' ||
          next === 'HAS_POINT_SUBSCRIPTION'),
      true,
    ) &&
  (fragment.type === 'SP123' ||
    fragment.type === 'S' ||
    fragment.type === 'C' ||
    fragment.type === 'RC' ||
    fragment.type === 'D' ||
    fragment.type === 'GSA' ||
    fragment.type === 'FGSA' ||
    fragment.type === 'SA' ||
    fragment.type === 'BBC' ||
    fragment.type === 'BBD' ||
    fragment.type === 'MBC' ||
    fragment.type === 'MBD' ||
    fragment.type === 'PBC' ||
    fragment.type === 'PBD' ||
    fragment.type === 'JO' ||
    fragment.type === 'ID' ||
    fragment.type === 'MD' ||
    fragment.type === 'WCS') &&
  typeof fragment.cardCost === 'number' &&
  typeof fragment.bulkCardCost === 'number' &&
  typeof fragment.giftDiscount === 'number' &&
  typeof fragment.isExpenseReloadRequired === 'boolean' &&
  typeof fragment.isRep === 'boolean' &&
  typeof fragment.dateJoined === 'string' &&
  typeof fragment.hasFullAccessContactManager === 'boolean' &&
  typeof fragment.hasFullAccessFreeAffiliate === 'boolean' &&
  typeof fragment.shouldDisplayUpsale === 'boolean' &&
  Array.isArray(fragment.activePlansAndAddons) &&
  fragment.activePlansAndAddons
    .slice(0, 5)
    .reduce(
      (accum: any, next: any) =>
        accum && (next === null || (isPlanFragment(next) as boolean)),
      true,
    ) &&
  (fragment.settings === null ||
    (isUserSettingsFragment(fragment.settings) as boolean)) &&
  (fragment.shippingAddress === null ||
    (isAddressFragment(fragment.shippingAddress) as boolean)) &&
  (fragment.sponsor === null ||
    (isMinimalSponsorFragment(fragment.sponsor) as boolean)) &&
  (fragment.storefront === null ||
    (isStorefront(fragment.storefront) as boolean)) &&
  (isPlanFragment(fragment.plan) as boolean) &&
  (fragment.stripeSource === null ||
    (isStripeSourceFragment(fragment.stripeSource) as boolean)) &&
  (fragment.stripeSubscription === null ||
    (isStripeSubscription(fragment.stripeSubscription) as boolean)) &&
  Array.isArray(fragment.subscriptions) &&
  fragment.subscriptions
    .slice(0, 5)
    .reduce(
      (accum: any, next: any) =>
        accum && (isStripeSubscription(next) as boolean),
      true,
    ) &&
  (isAmount(fragment.points) as boolean) &&
  (isAmount(fragment.expense) as boolean) &&
  (fragment.expenseReload === null ||
    (isExpenseReload(fragment.expenseReload) as boolean)) &&
  (fragment.verifiedPhoneNumber === null ||
    (fragment.verifiedPhoneNumber &&
      fragment.verifiedPhoneNumber.__typename === 'PhoneNumber' &&
      typeof fragment.verifiedPhoneNumber.nationalNumber === 'string')) &&
  (fragment.promptingsCoach === null ||
    (isPromptingsCoachFragment(fragment.promptingsCoach) as boolean))
